/**
 * OCC Benchmark 空间热力图（halo occ_benchmark 的 spatial 面板）。
 *
 * 5 m 分箱，base_link 坐标（x 向前、y 向左）。按「从车顶往下看、车头朝上」的视角画：
 * - 行是 x：上方 = 车前更远（下标倒序）；
 * - 列是 y：左侧 = 车左（y 为正），即 yBins 下标倒序——与 halo 报表的升序列相反，
 *   否则画面左右镜像。
 * 本车（原点 x=0、y=0）落在 x 的 [0,…)/[…,0) 两行与 y 的 [0,…)/[…,0) 两列的交界处，
 * 用车形标记画在那个格角上。
 *
 * 单元格数字为百分比；空格 = 该箱无数据（不在仿真覆盖范围 / 没有可平均的类），不是 0。
 */
import { fmtRatio } from '../format';
import { HeatmapReadingNote } from './OccHelp';

export interface HeatCell {
  x: number;
  y: number;
  value: number | null;
  /** hover 附加说明（如有效格子数） */
  note?: string;
}

interface OccHeatmapProps {
  xBins: string[];
  yBins: string[];
  cells: HeatCell[];
  /** 指标名，用于下方读法说明 */
  metric?: string;
}

/** 0~1 → 底色：浅灰到主色（#5e6ad2）按值线性加深。 */
const heatColor = (v: number): string => {
  const t = Math.max(0, Math.min(1, v));
  return `rgba(94, 106, 210, ${(0.06 + 0.84 * t).toFixed(3)})`;
};

/** ``[lo,hi)`` 形式的分箱标签 → 下界；解析失败返回 null。 */
const binLower = (label: string): number | null => {
  const m = /^\s*[[(]\s*(-?\d+(?:\.\d+)?)\s*,/.exec(label);
  return m ? Number(m[1]) : null;
};

/** 下界为 0 的分箱（1 起下标）；没有时返回 null（此时不画本车标记）。 */
const zeroBinIndex = (bins: string[]): number | null => {
  const i = bins.findIndex((b) => binLower(b) === 0);
  return i >= 0 ? i + 1 : null;
};

/** 俯视的小车（车头朝上），画在原点格角上。 */
function CarMarker() {
  return (
    <span className="occ-heat-car" title="本车位置（坐标原点，车头朝上）">
      <svg viewBox="0 0 14 22" width="14" height="22" aria-hidden="true">
        <rect x="1" y="1" width="12" height="20" rx="4" fill="#f08c00" stroke="#fff" strokeWidth="1.5" />
        <rect x="3.5" y="4.5" width="7" height="4" rx="1.2" fill="#fff" opacity="0.9" />
        <rect x="3.5" y="15" width="7" height="2.5" rx="1" fill="#fff" opacity="0.6" />
      </svg>
    </span>
  );
}

export default function OccHeatmap({ xBins, yBins, cells, metric }: OccHeatmapProps) {
  const byKey = new Map(cells.map((c) => [`${c.x}|${c.y}`, c]));
  const hasValue = cells.some((c) => c.value !== null && c.value !== undefined);
  if (!xBins.length || !yBins.length || !hasValue) {
    return <div className="pe-report-empty">无空间分箱数据</div>;
  }
  // x 前向：远处在上 → 下标倒序；y 向左为正：车左画在图左 → 下标倒序
  const xs = xBins.map((_, i) => i + 1).reverse();
  const ys = yBins.map((_, i) => i + 1).reverse();
  // 原点在「x 下界为 0 的行」的下边、「y 下界为 0 的列」的右边
  const carX = zeroBinIndex(xBins);
  const carY = zeroBinIndex(yBins);
  return (
    <div className="occ-heat-block">
      <div className="occ-heat-wrap">
        <table className="occ-heat">
          <thead>
            <tr>
              <th className="occ-heat-corner occ-heat-dir">↑ 车前</th>
              <th colSpan={ys.length} className="occ-heat-lr">
                <div className="occ-heat-lr-row">
                  <span>车左 ←</span>
                  <span className="occ-heat-lr-mid">左右距离（m，左正右负）</span>
                  <span>→ 车右</span>
                </div>
              </th>
            </tr>
            <tr>
              <th className="occ-heat-corner">前后距离（m）</th>
              {ys.map((y) => (
                <th key={y} className="occ-heat-ylab">
                  {yBins[y - 1]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {xs.map((x) => (
              <tr key={x}>
                <th className="occ-heat-xlab">{xBins[x - 1]}</th>
                {ys.map((y) => {
                  const c = byKey.get(`${x}|${y}`);
                  const v = c?.value;
                  const empty = v === null || v === undefined;
                  const isCar = x === carX && y === carY;
                  const tip = `前后 ${xBins[x - 1]} m · 左右 ${yBins[y - 1]} m：${
                    empty ? '不在覆盖范围 / 无数据' : fmtRatio(v)
                  }${c?.note ? `（${c.note}）` : ''}`;
                  return (
                    <td
                      key={y}
                      title={tip}
                      className={`occ-heat-cell${empty ? ' occ-heat-cell--empty' : ''}`}
                      style={
                        empty
                          ? undefined
                          : { background: heatColor(v), color: v > 0.55 ? '#fff' : '#1a1a1a' }
                      }
                    >
                      {empty ? '' : Math.round(v * 100)}
                      {isCar && <CarMarker />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th className="occ-heat-corner occ-heat-dir">↓ 车后</th>
              <th colSpan={ys.length} />
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="occ-heat-legend">
        <span>0%</span>
        <span className="occ-heat-legend-bar" />
        <span>100%</span>
        <span className="occ-heat-legend-car">
          <CarMarker /> 本车位置
        </span>
        <span>空白格 = 不评测</span>
      </div>
      <HeatmapReadingNote metric={metric} />
    </div>
  );
}
